import React from 'react';
import {ExternalDevicePowerConsumptionRating} from '@di-shared/features/diag/battery/useBatteryStatus';
import {drawArcPath} from '@di-shared/utils/drawArcPath';

interface ElectronicDeviceDetectionGraphProps {
  powerConsumptionRating: ExternalDevicePowerConsumptionRating;
}

enum GraphColor {
  LOW = 'var(--secondary)',
  NORMAL = 'var(--tertiary)',
  HIGH = 'var(--graph-gauge-red)',
  VERY_HIGH = 'var(--quaternary)',
  BACKGROUND = 'var(--surface-container-low)',
  TEXT = 'var(--on-surface-container-mid)',
}

function getSectionAngleBoundary(weightArray: number[], totalAngle: number) {
  const cumulativeSum = weightArray.reduce((acc: number[], curr) => {
    const lastSum = acc.length > 0 ? acc[acc.length - 1] : 0;
    acc.push(lastSum + curr);
    return acc;
  }, []);
  const totalSum = cumulativeSum[cumulativeSum.length - 1];
  return cumulativeSum.map((sum) => totalAngle * (sum / totalSum));
}

const OFFSET = 4;
const ELECTRONIC_DEVICE_DETECTION_GRAPH_RADIUS = 110;
const ELECTRONIC_DEVICE_DETECTION_GRAPH_CENTER_X = 239 / 2;
const ELECTRONIC_DEVICE_DETECTION_GRAPH_CENTER_Y = 136 - 22;

const SECTION_ANGLE_BOUNDARY_ZERO = 180;
const SECTION_ANGLE_BOUNDARY_ANGLE = getSectionAngleBoundary([0, 2, 3, 5, 6], 180);

/**
 * SECTION_ANGLE_BOUNDARY 시작: +x,0
 * 방향: -theta
 */
const SECTION_ANGLE_BOUNDARY = Array.from({length: 4}).map((_, i) => ({
  START: SECTION_ANGLE_BOUNDARY_ZERO + SECTION_ANGLE_BOUNDARY_ANGLE[i] + OFFSET,
  END: SECTION_ANGLE_BOUNDARY_ZERO + SECTION_ANGLE_BOUNDARY_ANGLE[i + 1] - OFFSET,
}));

interface ArcProps {
  startAngle: number;
  endAngle: number;
  color: string;
  strokeWidth?: number;
}

function Arc({startAngle, endAngle, color, strokeWidth = 8}: ArcProps) {
  return (
    <path
      d={drawArcPath(
        startAngle,
        endAngle,
        ELECTRONIC_DEVICE_DETECTION_GRAPH_RADIUS,
        ELECTRONIC_DEVICE_DETECTION_GRAPH_CENTER_X,
        ELECTRONIC_DEVICE_DETECTION_GRAPH_CENTER_Y
      )}
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
    />
  );
}

const indicatorIndexMapping: {[key in ExternalDevicePowerConsumptionRating]: number} = {
  [ExternalDevicePowerConsumptionRating.LOW]: 0,
  [ExternalDevicePowerConsumptionRating.NORMAL]: 1,
  [ExternalDevicePowerConsumptionRating.HIGH]: 2,
  [ExternalDevicePowerConsumptionRating.VERY_HIGH]: 3,
};

const color = [GraphColor.LOW, GraphColor.NORMAL, GraphColor.HIGH, GraphColor.VERY_HIGH];

/**
 * [2024-11-29:Jeyoung Lee] FIXME: 단위 변환에 대한 논의가 끝나면 없어져야할 주석
 * 0W, 8W 단위 수정하기
 */
function ElectronicDeviceDetectionGraph({powerConsumptionRating}: ElectronicDeviceDetectionGraphProps) {
  const ratingIndex = indicatorIndexMapping[powerConsumptionRating];
  return (
    <svg
      width="239"
      height="136"
      viewBox="0 0 239 136"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <text
        x="10"
        y="130"
        alignmentBaseline="central"
        textAnchor="middle"
        fill={GraphColor.TEXT}
        fontSize="11px"
      >
        0W
      </text>
      <text
        x="230"
        y="130"
        alignmentBaseline="central"
        textAnchor="middle"
        fill={GraphColor.TEXT}
        fontSize="11px"
      >
        8W
      </text>
      {SECTION_ANGLE_BOUNDARY.map((section, index) => (
        <Arc
          key={section.START.toString() + section.END}
          startAngle={section.START}
          endAngle={section.END}
          color={index <= ratingIndex ? color[index] : GraphColor.BACKGROUND}
        />
      ))}
    </svg>
  );
}

export default ElectronicDeviceDetectionGraph;
